import { createPremiumIcon, node } from "~/util/dom";
import { _ } from "~/i18n";
import { ISDSIcon } from "@sznds/icons";
import { ISDSIconMulti, TActivityType } from "~/interfaces";
import { MYMAPS_PLACES_DROP, ROUTE } from "../sds-compatible-icons";
import createSDSIcon from "../icon";
import { ICON_DISTANCEMETER_24 } from "../icons";
import { activityTypeToIcon, activityTypeToName } from "~/activities/util";
import { activityTypes2criterions } from "~/route/utils";

export interface IMyMapsFilterRouteTypesFilterData {
	hasPlaces: boolean;
	hasDistanceMeter: boolean;
	places: boolean;
	distancemeter: boolean;
	allRoutes: boolean;
	allTypes: Array<TActivityType>;
	selTypes: Array<TActivityType>;
	allCriterions: Array<number>;
	selCriterions: Array<number>;
}

interface IOpts {
	isPremiumUser?: boolean;
	hasPlaces?: boolean;
	hasDistanceMeter?: boolean;
	filter?: IMyMapsFilterRouteTypesFilterData;
	onChange?: (data: IMyMapsFilterRouteTypesFilterData, count: number) => void;
	onNonPremiumClick?: () => void;
}

interface ICreateOneLine {
	sdsIcon: ISDSIcon | ISDSIconMulti;
	text: string;
	isPremiumUser?: boolean;
	checked?: boolean;
	onChange?: (checked: boolean) => void;
	onNonPremiumClick?: () => void;
}

function createOneLine({
	sdsIcon,
	text,
	checked = false,
	isPremiumUser = false,
	onChange = () => {},
	onNonPremiumClick = () => {},
}: ICreateOneLine) {
	const lineElem = node("div", { className: "mymaps__filterRouteTypes__line" });
	const leftPart = node("span", { className: "mymaps__filterRouteTypes__lineLeftPart" });
	const rightPart = node("span", { className: "mymaps__filterRouteTypes__lineRightPart" });
	const toggle = node("mapy-toggle");
	const textElem = node("span", { className: "mymaps__filterRouteTypes__lineText", textContent: text });

	leftPart.append(createSDSIcon({ symbol: sdsIcon, className: "mymaps__filterRouteTypes__lineIcon" }));
	leftPart.append(textElem);
	toggle.checked = checked;
	toggle.addEventListener("change", () => {
		onChange(toggle.checked);
	});
	leftPart.addEventListener("click", () => {
		if (isPremiumUser) {
			toggle.checked = !toggle.checked;
			onChange(toggle.checked);
		}
	});
	lineElem.append(leftPart, rightPart);

	if (isPremiumUser) {
		rightPart.append(toggle);
	} else {
		leftPart.classList.add("non-premium");
		rightPart.append(createPremiumIcon());
		lineElem.addEventListener("click", () => {
			onNonPremiumClick();
		});
	}

	return {
		container: lineElem,
		setChecked: (newChecked: boolean) => {
			if (isPremiumUser) {
				toggle.checked = newChecked;
			}
		},
	};
}

export default class MyMapsFilterRouteTypes {
	_opts: IOpts;

	_dom: {
		container: HTMLDivElement;
		doneBtn: HTMLMapyRouterButtonElement;
	};

	_filter: IMyMapsFilterRouteTypesFilterData;

	_origFilter: IMyMapsFilterRouteTypesFilterData;

	_categories: Array<ReturnType<typeof createOneLine>>;

	_types: Array<ReturnType<typeof createOneLine>>;

	constructor(opts: IOpts) {
		this._opts = {
			hasPlaces: false,
			hasDistanceMeter: false,
			isPremiumUser: false,
			onChange: () => {},
			onNonPremiumClick: () => {},
			...opts,
		};
		this._filter = {
			hasPlaces: this._opts.hasPlaces,
			hasDistanceMeter: this._opts.hasDistanceMeter,
			places: this._opts.hasPlaces,
			distancemeter: this._opts.hasDistanceMeter,
			allRoutes: true,
			allTypes: [],
			selTypes: [],
			allCriterions: [],
			selCriterions: [],
			...this._opts.filter
				? this._opts.filter
				: {},
		};
		this._origFilter = structuredClone(this._filter);
		this._categories = [];
		this._types = [];

		// styly nechame
		this._dom = {
			container: node("div", { className: "mymaps__filterRouteTypes" }),
			doneBtn: node("mapy-router-button", {}, _("done")),
		};

		this._build();
	}

	get container() {
		return this._dom.container;
	}

	get filter() {
		return structuredClone(this._filter);
	}

	get count() {
		const isChanged = (this._opts.hasPlaces && !this._filter.places)
			|| (this._opts.hasDistanceMeter && !this._filter.distancemeter)
			|| this._filter.allTypes.length !== this._filter.selTypes.length;
		let count = -1;

		if (isChanged) {
			count = 0;

			if (this._opts.hasPlaces && this._filter.places) {
				count++;
			}

			if (this._opts.hasDistanceMeter && this._filter.distancemeter) {
				count++;
			}

			count += this._filter.selTypes.length;
		}

		return count;
	}

	reset() {
		this._filter = structuredClone(this._origFilter);

		for (const category of this._categories) {
			category.setChecked(true);
		}

		for (const type of this._types) {
			type.setChecked(true);
		}
	}

	_build() {
		if (this._opts.hasPlaces) {
			const placesOpt = createOneLine({
				sdsIcon: MYMAPS_PLACES_DROP,
				text: _("mymap.filters.places"),
				isPremiumUser: this._opts.isPremiumUser,
				checked: this._filter.places,
				onChange: checked => {
					this._filter.places = checked;
				},
				onNonPremiumClick: this._opts.onNonPremiumClick,
			});

			this._dom.container.append(placesOpt.container);
			this._categories.push(placesOpt);
		}

		if (this._opts.hasDistanceMeter) {
			const distanceMeterOpt = createOneLine({
				sdsIcon: ICON_DISTANCEMETER_24,
				text: _("distanceMeter.title"),
				isPremiumUser: this._opts.isPremiumUser,
				checked: this._filter.distancemeter,
				onChange: checked => {
					this._filter.distancemeter = checked;
				},
				onNonPremiumClick: this._opts.onNonPremiumClick,
			});

			this._dom.container.append(distanceMeterOpt.container);
			this._categories.push(distanceMeterOpt);
		}

		if (this._opts.hasPlaces || this._opts.hasDistanceMeter) {
			this._dom.container.append(node("div", { className: "mymaps__filterRouteTypes__separator" }));
		}

		const allRoutesOpt = createOneLine({
			sdsIcon: ROUTE,
			text: _("mymap.filters.allRoutes"),
			isPremiumUser: this._opts.isPremiumUser,
			checked: this._filter.allRoutes,
			onChange: checked => {
				this._filter.allRoutes = checked;
				this._filter.selTypes = checked
					? this._filter.allTypes.slice()
					: [];
				this._updateRouteCriterions();

				for (const type of this._types) {
					type.setChecked(checked);
				}
			},
			onNonPremiumClick: this._opts.onNonPremiumClick,
		});

		// vsechny trasy vzdycky
		this._dom.container.append(allRoutesOpt.container);
		this._categories.push(allRoutesOpt);

		for (const type of this._filter.allTypes) {
			const newTypeLine = createOneLine({
				sdsIcon: activityTypeToIcon(type),
				text: activityTypeToName(type),
				isPremiumUser: this._opts.isPremiumUser,
				checked: this._filter.selTypes.includes(type),
				onChange: checked => {
					if (checked && !this._filter.selTypes.includes(type)) {
						this._filter.selTypes.push(type);
					} else if (!checked) {
						const ind = this._filter.selTypes.indexOf(type);

						if (ind !== -1) {
							this._filter.selTypes.splice(ind, 1);
						}
					}

					if (this._filter.allRoutes && this._filter.selTypes.length !== this._filter.allTypes.length) {
						allRoutesOpt.setChecked(false);
						this._filter.allRoutes = false;
					} else if (!this._filter.allRoutes && this._filter.selTypes.length === this._filter.allTypes.length) {
						allRoutesOpt.setChecked(true);
						this._filter.allRoutes = true;
					}

					this._updateRouteCriterions();
				},
				onNonPremiumClick: this._opts.onNonPremiumClick,
			});

			this._types.push(newTypeLine);
			this._dom.container.append(newTypeLine.container);
		}

		this._dom.container.append(this._dom.doneBtn);
		this._dom.doneBtn.addEventListener("click", () => {
			this._done();
		});
	}

	_updateRouteCriterions() {
		const selTypes = this._filter.selTypes;

		this._filter.selCriterions = activityTypes2criterions(selTypes);
	}

	_done() {
		this._opts.onChange(this._filter, this.count);
	}
}
